<script setup lang="ts">
import { useStorage } from "@vueuse/core";
import { ElMessageBox } from "element-plus";
import { computed, onMounted, ref } from "vue";
import {
  ComplianceItemStatus,
  FormType,
  ItemDefinition,
  ItemDefinitionListDto,
} from "~/api";
import ItemDefinitionDetailEditor from "~/Compliance/components/ItemDefinitionDetailEditor.vue";
import ComplianceItemSecurity from "~/Compliance/components/ItemDefinitionSecurityEditor.vue";
import ComplianceItemUsage from "~/Compliance/components/ItemDefinitionUsageViewer.vue";
import FormDesigner from "~/NewFormBuilder/components/Designer/FormDesigner.vue";
import { useMyInfoStore } from "~/stores/MyInfoStore";
import { apiClient } from "~/utils/apiClient";
import { drawerWidth, emptyGuid } from "~/utils/constants";

type mainType = ItemDefinition;
type listItemType = ItemDefinitionListDto;

const myInfoStore = useMyInfoStore();

const activeItemTab = useStorage("activeItemTab", "general");

const loading = ref(true);
const drawerOpen = ref(true);
const props = defineProps<{
  itemDefinitionListDto: listItemType;
}>();

const emit = defineEmits<{
  (e: "closeDrawer"): void;
  (e: "updated", value: mainType): void;
  (e: "added", value: mainType): void;
  (e: "deleted", value: mainType): void;
}>();

const isUsable = computed(() => {
  return itemDefinition.value.guid !== emptyGuid;
});

const itemDefinition = ref<mainType>({
  guid: "",
  sysOrgGuid: "",
  formDefinitionGuid: "",
  name: "",
  forEntityDefinitionGuid: "",
  status: ComplianceItemStatus.UNUSED,
  triggers: [],
  statusList: [],
});
const lastSavedJson = ref<string>("");

// const form = ref<FormDefinition>();
// const rendererKey = ref(0);

// watch(
//   () => form.value,
//   () => {
//     rendererKey.value++;
//   },
// );

const updateLastUpdated = () => {
  lastSavedJson.value = JSON.stringify(itemDefinition.value);
};

onMounted(async () => {
  const guidWanted = props.itemDefinitionListDto.guid;
  if (guidWanted?.startsWith("new") || !guidWanted) {
    // create a new item
    activeItemTab.value = "general";

    itemDefinition.value = {
      guid: emptyGuid,
      sysOrgGuid: emptyGuid,
      formDefinitionGuid: emptyGuid,
      name: "",
      forEntityDefinitionGuid: "",
      status: ComplianceItemStatus.UNUSED,
      statusList: [],
      formDefinition: {
        guid: emptyGuid,
        name: "Default",
        sysOrgGuid: emptyGuid,
        formType: FormType.COMPLIANCE_ITEM_DEFINITION,
        metaInfo: { multipleInstancesAllowed: true },
        rules: [],
        sectionRules: [],
        formRules: [],
        components: [
          // {
          //   guid: emptyGuid,
          //   type: FormElementType.SECTION,
          //   formDefinitionGuid: "",
          //   parentGuid: "",
          //   questionCode: "",
          //   properties: {
          //     indentLevel: undefined,
          //     title: undefined,
          //     comment: undefined,
          //     specialQuestion: undefined,
          //     isOptional: undefined,
          //     showOptionality: undefined,
          //     isReadOnly: undefined,
          //     sensitivity: undefined,
          //     carryForward: undefined,
          //   },
          //   events: [],
          // },
        ],
      },
      triggers: [],
    };
    updateLastUpdated();
    loading.value = false;
    return;
  }
  apiClient.itemDefinition
    .getItemDefinition(guidWanted, myInfoStore.currentSysOrgGuid)
    .then((res) => {
      itemDefinition.value = res;
      updateLastUpdated();
      loading.value = false;
    });
});

/** 'true' if any changes have been made, and a save is needed */
const saveNeeded = computed(() => {
  const changed = JSON.stringify(itemDefinition.value) !== lastSavedJson.value;

  return changed;
});

const handleClose = async () => {
  if (saveNeeded.value) {
    ElMessageBox.confirm("You have unsaved changes.", "Unsaved Changes", {
      cancelButtonText: "Discard",
      confirmButtonText: "Continue Editing",
      closeOnPressEscape: false,
      closeOnClickModal: false,
      showClose: false,
      type: "warning",
    })
      // If the user confirms
      .then(() => {
        // do nothing
      })
      // If the user cancels
      .catch(() => {
        emit("closeDrawer");
      });
  } else {
    emit("closeDrawer");
  }
};

const handleNewItemAdded = (item: mainType) => {
  itemDefinition.value = item;
  updateLastUpdated();
  emit("added", item);
};

const handleItemUpdated = (item: mainType) => {
  // passthrough
  itemDefinition.value = item;
  updateLastUpdated();
  emit("updated", item);
};

const handleItemDeleted = (item: mainType) => {
  itemDefinition.value = item;
  emit("deleted", item);
};
</script>

<template>
  <el-drawer
    v-if="itemDefinition"
    :model-value="drawerOpen"
    :title="itemDefinition.name || '\u00A0'"
    :before-close="handleClose"
    :destroy-on-close="true"
    direction="rtl"
    :size="drawerWidth"
  >
    <div v-loading="loading" class="item-designer">
      <el-tabs v-model="activeItemTab" type="card" class="demo-tabs">
        <el-tab-pane label="General" name="general">
          <ItemDefinitionDetailEditor
            v-if="itemDefinition"
            :model-value="itemDefinition"
            @added="handleNewItemAdded"
            @updated="handleItemUpdated"
            @deleted="handleItemDeleted"
          />
        </el-tab-pane>
        <el-tab-pane v-if="isUsable" label="Form" name="questions" lazy>
          <div v-if="itemDefinition?.formDefinition" class="formContainer">
            <FormDesigner
              v-model="itemDefinition.formDefinition"
              :compliance-item="true"
              @form-saved="updateLastUpdated"
            />
          </div>
        </el-tab-pane>
        <el-tab-pane v-if="isUsable" label="Security" name="security">
          <ComplianceItemSecurity
            v-if="itemDefinition"
            class="security"
            :model-value="itemDefinition"
        /></el-tab-pane>
        <el-tab-pane v-if="isUsable" label="Usage" name="usage" lazy>
          <ComplianceItemUsage
            v-if="itemDefinition"
            class="details"
            :model-value="itemDefinition"
          />
        </el-tab-pane>
      </el-tabs>
    </div>
  </el-drawer>
</template>

<style lang="less" scoped>
.item-designer {
  .renderer {
    display: grid;
    grid-template-areas:
      "renderer-inner edit edit"
      "renderer-inner details security";
    grid-template-columns: 2fr 1fr 1fr;
    gap: 1em;
    border-left: 1px solid #ebeef5;

    table {
      width: 100%;
      td {
        padding: 0.5em;
        border-bottom: 1px solid #ebeef5;
      }
    }
  }
}
</style>
